// src/components/Activities/SpeakersForm.jsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SpeakersForm.css';

const SpeakersForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepErrors, setStepErrors] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    bio: null,
    cv: null,
    photo: null,
    presentation: null,
    slides: null,
    profile: null
  });

  // File input refs
  const bioInputRef = useRef(null);
  const cvInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const presentationInputRef = useRef(null);
  const slidesInputRef = useRef(null);
  const profileInputRef = useRef(null);

  // ===== FORM STATE =====
  const [formData, setFormData] = useState({
    // SECTION A: Personal Information
    title: '',
    otherTitle: '',
    fullName: '',
    officialDesignation: '',
    organization: '',
    country: '',
    phone: '',
    email: '',

    // SECTION B: Participation Category
    participationCategory: [],
    otherParticipation: '',

    // SECTION C: Session Information
    presentationTitle: '',
    thematicArea: '',
    modeOfParticipation: '',

    // SECTION D: Professional Profile
    professionalBio: '',

    // SECTION F: Supporting Documents - Checkboxes
    bioSubmitted: false,
    cvSubmitted: false,
    photoSubmitted: false,
    presentationSubmitted: false,
    slidesSubmitted: false,
    profileSubmitted: false,

    // Special Requirements
    specialRequirements: '',

    // SECTION G: Participation Confirmation
    confirmationName: '',
    confirmationAgreement: false,

    // SECTION H: Media Consent
    mediaConsent: '',
    mediaLimitations: '',

    // SECTION I: Declaration
    declarationName: '',
    declarationDesignation: '',
    declarationOrganization: '',
    declarationSignature: '',
    declarationDate: '',

    // Optional Endorsement
    endorsingOfficial: '',
    endorsingInstitution: '',
    endorsementSignature: '',
    endorsementDate: ''
  });

  // ===== FILE UPLOAD FUNCTIONS =====
  const uploadFile = async (file, fileType) => {
    if (!file) return null;

    try {
      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));

      const fileName = `${Date.now()}_${fileType}_${file.name}`;
      const filePath = `speakers_forms/${fileName}`;

      const { error } = await supabase.storage
        .from('speakers-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        alert(`Failed to upload ${fileType}: ${error.message}`);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('speakers-documents')
        .getPublicUrl(filePath);

      setUploadedFiles(prev => ({ 
        ...prev, 
        [fileType]: { 
          name: file.name, 
          url: urlData.publicUrl,
          size: file.size,
          type: file.type
        } 
      }));

      return urlData.publicUrl;

    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload ${fileType}: ${error.message}`);
      return null;
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleBioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'bio');
    if (url) {
      setFormData(prev => ({ ...prev, bioSubmitted: true }));
    }
    e.target.value = '';
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'cv');
    if (url) {
      setFormData(prev => ({ ...prev, cvSubmitted: true }));
    }
    e.target.value = '';
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'photo');
    if (url) {
      setFormData(prev => ({ ...prev, photoSubmitted: true }));
    }
    e.target.value = '';
  };

  const handlePresentationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'presentation');
    if (url) {
      setFormData(prev => ({ ...prev, presentationSubmitted: true }));
    }
    e.target.value = '';
  };

  const handleSlidesUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'slides');
    if (url) {
      setFormData(prev => ({ ...prev, slidesSubmitted: true }));
    }
    e.target.value = '';
  };

  const handleProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'profile');
    if (url) {
      setFormData(prev => ({ ...prev, profileSubmitted: true }));
    }
    e.target.value = '';
  };

  const getUploadStatus = (fileType) => {
    if (uploadingFiles[fileType]) {
      return <span className="upload-status uploading">⏳ Uploading...</span>;
    }
    if (uploadedFiles[fileType]) {
      return <span className="upload-status success">✅ {uploadedFiles[fileType].name}</span>;
    }
    return null;
  };

  // ===== VALIDATION FUNCTIONS =====
  const validateStep1 = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Full Name is required';
    if (!formData.officialDesignation.trim()) errors.officialDesignation = 'Official Designation is required';
    if (!formData.organization.trim()) errors.organization = 'Organization is required';
    if (!formData.country.trim()) errors.country = 'Country is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    if (formData.participationCategory.length === 0 && !formData.otherParticipation.trim()) {
      errors.participationCategory = 'Please select at least one participation category';
    }
    return errors;
  };

  const validateStep3 = () => {
    const errors = {};
    if (!formData.presentationTitle.trim()) errors.presentationTitle = 'Presentation title is required';
    if (!formData.thematicArea) errors.thematicArea = 'Please select a thematic area';
    if (!formData.modeOfParticipation) errors.modeOfParticipation = 'Please select mode of participation';
    return errors;
  };

  const validateStep4 = () => {
    const errors = {};
    if (!formData.professionalBio.trim() || formData.professionalBio.trim().length < 50) {
      errors.professionalBio = 'Please provide a professional biography of at least 50 characters';
    }
    return errors;
  };

  const validateStep5 = () => {
    const errors = {};
    const anyDocumentSelected = formData.bioSubmitted || formData.cvSubmitted || 
                                formData.photoSubmitted || formData.presentationSubmitted || 
                                formData.slidesSubmitted || formData.profileSubmitted;
    if (!anyDocumentSelected) {
      errors.documents = 'Please select at least one document to submit';
    }
    return errors;
  };

  const validateStep6 = () => {
    const errors = {};
    if (!formData.confirmationName.trim()) errors.confirmationName = 'Full Name is required for confirmation';
    if (!formData.confirmationAgreement) errors.confirmationAgreement = 'Please confirm your participation';
    return errors;
  };

  const validateStep7 = () => {
    const errors = {};
    if (!formData.mediaConsent) errors.mediaConsent = 'Please select a media consent option';
    return errors;
  };

  const validateStep8 = () => {
    const errors = {};
    if (!formData.declarationName.trim()) errors.declarationName = 'Full Name is required';
    if (!formData.declarationDesignation.trim()) errors.declarationDesignation = 'Official Designation is required';
    if (!formData.declarationOrganization.trim()) errors.declarationOrganization = 'Organization is required';
    if (!formData.declarationSignature.trim()) errors.declarationSignature = 'Signature is required';
    return errors;
  };

  const validateStep9 = () => {
    return {};
  };

  const validateStep = (step) => {
    switch(step) {
      case 1: return validateStep1();
      case 2: return validateStep2();
      case 3: return validateStep3();
      case 4: return validateStep4();
      case 5: return validateStep5();
      case 6: return validateStep6();
      case 7: return validateStep7();
      case 8: return validateStep8();
      case 9: return validateStep9();
      default: return {};
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'participationCategory') {
        setFormData(prev => {
          const array = prev[name] || [];
          if (checked) {
            return { ...prev, [name]: [...array, value] };
          } else {
            return { ...prev, [name]: array.filter(item => item !== value) };
          }
        });
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (stepErrors[name]) {
      setStepErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const renderError = (fieldName) => {
    if (stepErrors[fieldName]) {
      return <span className="field-error">{stepErrors[fieldName]}</span>;
    }
    return null;
  };

  const nextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    setStepErrors({});
    if (currentStep < 10) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setStepErrors({});
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    for (let step = 1; step <= 9; step++) {
      const errors = validateStep(step);
      if (Object.keys(errors).length > 0) {
        setStepErrors(errors);
        setCurrentStep(step);
        alert('Please complete all required fields before submitting.');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const bioUrl = uploadedFiles.bio?.url || null;
      const cvUrl = uploadedFiles.cv?.url || null;
      const photoUrl = uploadedFiles.photo?.url || null;
      const presentationUrl = uploadedFiles.presentation?.url || null;
      const slidesUrl = uploadedFiles.slides?.url || null;
      const profileUrl = uploadedFiles.profile?.url || null;

      const { error } = await supabase
        .from('speakers_forms')
        .insert([{
          title: formData.title,
          other_title: formData.otherTitle,
          full_name: formData.fullName,
          official_designation: formData.officialDesignation,
          organization: formData.organization,
          country: formData.country,
          phone: formData.phone,
          email: formData.email,
          participation_category: formData.participationCategory,
          other_participation: formData.otherParticipation,
          presentation_title: formData.presentationTitle,
          thematic_area: formData.thematicArea,
          mode_of_participation: formData.modeOfParticipation,
          professional_bio: formData.professionalBio,
          bio_submitted: formData.bioSubmitted,
          cv_submitted: formData.cvSubmitted,
          photo_submitted: formData.photoSubmitted,
          presentation_submitted: formData.presentationSubmitted,
          slides_submitted: formData.slidesSubmitted,
          profile_submitted: formData.profileSubmitted,
          bio_url: bioUrl,
          cv_url: cvUrl,
          photo_url: photoUrl,
          presentation_url: presentationUrl,
          slides_url: slidesUrl,
          profile_url: profileUrl,
          special_requirements: formData.specialRequirements,
          confirmation_name: formData.confirmationName,
          confirmation_agreement: formData.confirmationAgreement,
          media_consent: formData.mediaConsent,
          media_limitations: formData.mediaLimitations,
          declaration_name: formData.declarationName,
          declaration_designation: formData.declarationDesignation,
          declaration_organization: formData.declarationOrganization,
          declaration_signature: formData.declarationSignature,
          declaration_date: formData.declarationDate,
          endorsing_official: formData.endorsingOfficial,
          endorsing_institution: formData.endorsingInstitution,
          endorsement_signature: formData.endorsementSignature,
          endorsement_date: formData.endorsementDate,
          status: 'pending'
        }]);

      if (error) throw error;

      setSubmitSuccess(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    const totalSteps = 10;
    return (
      <div className="form-progress">
        <div className="progress-bar-track">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          ></div>
        </div>
        <div className="step-indicators">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
            <div 
              key={step}
              className={`step-dot ${step === currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
              onClick={() => {
                if (step < currentStep) {
                  setCurrentStep(step);
                  setStepErrors({});
                }
              }}
              style={{ cursor: step < currentStep ? 'pointer' : 'default' }}
            >
              <span className="step-number">{step}</span>
            </div>
          ))}
        </div>
        <div className="step-labels">
          <span>Personal</span>
          <span>Category</span>
          <span>Session</span>
          <span>Bio</span>
          <span>Documents</span>
          <span>Confirm</span>
          <span>Media</span>
          <span>Declaration</span>
          <span>Endorsement</span>
          <span>Submit</span>
        </div>
      </div>
    );
  };

  // ===== RENDER STEP 1: Personal Information =====
  const renderStep1 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION A: Personal and Professional Information</h2>
        <p>Please provide your personal and contact details</p>
      </div>
      
      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-grid">
        <div className="form-group full-width">
          <label>Title</label>
          <div className="title-options">
            {['H.E.', 'Hon.', 'Amb.', 'Prof.', 'Dr.', 'Mr.', 'Mrs.', 'Ms.'].map(title => (
              <label key={title} className="title-option">
                <input
                  type="radio"
                  name="title"
                  value={title}
                  checked={formData.title === title}
                  onChange={handleInputChange}
                />
                <span>{title}</span>
              </label>
            ))}
            <div className="title-other">
              <input
                type="text"
                name="otherTitle"
                placeholder="Other"
                value={formData.otherTitle}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className={`form-group ${stepErrors.fullName ? 'has-error' : ''}`}>
          <label>Full Name *</label>
          <input
            type="text"
            name="fullName"
            placeholder="Enter your full name"
            value={formData.fullName}
            onChange={handleInputChange}
            required
          />
          {renderError('fullName')}
        </div>

        <div className={`form-group ${stepErrors.officialDesignation ? 'has-error' : ''}`}>
          <label>Official Designation / Position *</label>
          <input
            type="text"
            name="officialDesignation"
            placeholder="Enter your official designation"
            value={formData.officialDesignation}
            onChange={handleInputChange}
            required
          />
          {renderError('officialDesignation')}
        </div>

        <div className={`form-group full-width ${stepErrors.organization ? 'has-error' : ''}`}>
          <label>Ministry, Department, Agency, Institution, Organisation or Company *</label>
          <input
            type="text"
            name="organization"
            placeholder="Enter organization name"
            value={formData.organization}
            onChange={handleInputChange}
            required
          />
          {renderError('organization')}
        </div>

        <div className={`form-group ${stepErrors.country ? 'has-error' : ''}`}>
          <label>Country *</label>
          <input
            type="text"
            name="country"
            placeholder="Enter your country"
            value={formData.country}
            onChange={handleInputChange}
            required
          />
          {renderError('country')}
        </div>

        <div className={`form-group ${stepErrors.phone ? 'has-error' : ''}`}>
          <label>Telephone / WhatsApp Number *</label>
          <input
            type="tel"
            name="phone"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleInputChange}
            required
          />
          {renderError('phone')}
        </div>

        <div className={`form-group ${stepErrors.email ? 'has-error' : ''}`}>
          <label>Official Email Address *</label>
          <input
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
          {renderError('email')}
        </div>
      </div>
    </div>
  );

  // ===== RENDER STEP 2: Participation Category =====
  const renderStep2 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION B: Participation Category</h2>
        <p>Please indicate your proposed or confirmed role</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`form-group ${stepErrors.participationCategory ? 'has-error' : ''}`}>
        <div className="checkbox-grid">
          {[
            'Keynote/Plenary Speaker',
            'Guest Speaker',
            'Panellist',
            'Session Chair/Moderator',
            'Technical Presenter',
            'Resource Person/Consultant',
            'Government/Institutional Representative',
            'Diplomatic Mission Representative',
            'Development Partner Representative',
            'Private-Sector Representative',
            'Project Promoter/Investor',
            'Institutional Focal Person/Point of Contact'
          ].map(category => (
            <label key={category} className="checkbox-option">
              <input
                type="checkbox"
                name="participationCategory"
                value={category}
                checked={formData.participationCategory.includes(category)}
                onChange={handleInputChange}
              />
              <span>{category}</span>
            </label>
          ))}
        </div>
        <div className="other-input">
          <input
            type="text"
            name="otherParticipation"
            placeholder="Other participation category"
            value={formData.otherParticipation}
            onChange={handleInputChange}
          />
        </div>
        {renderError('participationCategory')}
      </div>
    </div>
  );

  // ===== RENDER STEP 3: Session Information =====
  const renderStep3 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION C: Session or Presentation Information</h2>
        <p>Provide details about your proposed session</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`form-group ${stepErrors.presentationTitle ? 'has-error' : ''}`}>
        <label>Proposed Presentation, Intervention or Session Title *</label>
        <input
          type="text"
          name="presentationTitle"
          placeholder="Enter your presentation title"
          value={formData.presentationTitle}
          onChange={handleInputChange}
        />
        {renderError('presentationTitle')}
      </div>

      <div className={`form-group ${stepErrors.thematicArea ? 'has-error' : ''}`}>
        <label>Preferred Thematic Area *</label>
        <div className="radio-grid">
          {[
            'Infrastructure and Regional Connectivity',
            'Climate Change, Adaptation and Resilience',
            'Green Investment and Climate Finance',
            'Energy Security and Energy Transition',
            'Security, Peace and Sustainable Development',
            'Agriculture, Food Security and Agro-Industrial Development',
            'Trade, Investment, Industrialisation and AfCFTA',
            'Transport, Logistics and Economic Corridors',
            'Digital Infrastructure, Innovation and Technology',
            'Healthcare, Education and Social Infrastructure',
            'Project Preparation, Bankability and Investment Mobilisation'
          ].map(area => (
            <label key={area} className="radio-option-card">
              <input
                type="radio"
                name="thematicArea"
                value={area}
                checked={formData.thematicArea === area}
                onChange={handleInputChange}
              />
              <span>{area}</span>
            </label>
          ))}
        </div>
        {renderError('thematicArea')}
      </div>

      <div className={`form-group ${stepErrors.modeOfParticipation ? 'has-error' : ''}`}>
        <label>Mode of Participation *</label>
        <div className="radio-group">
          {['In Person', 'Virtual', 'Hybrid', 'To Be Confirmed'].map(mode => (
            <label key={mode} className="radio-option">
              <input
                type="radio"
                name="modeOfParticipation"
                value={mode}
                checked={formData.modeOfParticipation === mode}
                onChange={handleInputChange}
              />
              <span>{mode}</span>
            </label>
          ))}
        </div>
        {renderError('modeOfParticipation')}
      </div>
    </div>
  );

  // ===== RENDER STEP 4: Professional Profile =====
  const renderStep4 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION D: Professional Profile</h2>
        <p>Please provide a concise professional biography for official use</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`form-group ${stepErrors.professionalBio ? 'has-error' : ''}`}>
        <label>Professional Biography (150–200 words) *</label>
        <textarea
          name="professionalBio"
          placeholder="Provide a concise professional biography for official introduction, Summit publications, programme materials and approved media use."
          value={formData.professionalBio}
          onChange={handleInputChange}
          rows={8}
        />
        <small className="char-count">
          {formData.professionalBio.length} characters 
          {formData.professionalBio.length < 50 && ' (Minimum 50 characters recommended)'}
        </small>
        {renderError('professionalBio')}
      </div>
    </div>
  );

  // ===== RENDER STEP 5: Documents (FIXED - with type="button") =====
  const renderStep5 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION F: Required Supporting Documents</h2>
        <p>Please indicate and upload the documents you are submitting</p>
        <p className="step-note">Click "Upload" to select a file. You'll see the file name once uploaded.</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`form-group ${stepErrors.documents ? 'has-error' : ''}`}>
        <div className="document-upload-grid">
          {/* Professional Biography */}
          <div className="document-option">
            <div className="document-icon">📋</div>
            <label>
              <input
                type="checkbox"
                name="bioSubmitted"
                checked={formData.bioSubmitted}
                onChange={handleInputChange}
              />
              <span>Professional Biography/Profile</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => bioInputRef.current?.click()}
              disabled={uploadingFiles.bio}
            >
              {uploadingFiles.bio ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={bioInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleBioUpload}
            />
            {getUploadStatus('bio')}
          </div>

          {/* CV */}
          <div className="document-option">
            <div className="document-icon">📄</div>
            <label>
              <input
                type="checkbox"
                name="cvSubmitted"
                checked={formData.cvSubmitted}
                onChange={handleInputChange}
              />
              <span>Current Curriculum Vitae or Professional Résumé</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => cvInputRef.current?.click()}
              disabled={uploadingFiles.cv}
            >
              {uploadingFiles.cv ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={cvInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleCVUpload}
            />
            {getUploadStatus('cv')}
          </div>

          {/* Professional Photograph */}
          <div className="document-option">
            <div className="document-icon">🖼️</div>
            <label>
              <input
                type="checkbox"
                name="photoSubmitted"
                checked={formData.photoSubmitted}
                onChange={handleInputChange}
              />
              <span>Recent High-Resolution Professional Photograph</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingFiles.photo}
            >
              {uploadingFiles.photo ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={photoInputRef}
              style={{ display: 'none' }}
              accept=".jpg,.jpeg,.png,.gif,.webp"
              onChange={handlePhotoUpload}
            />
            {getUploadStatus('photo')}
          </div>

          {/* Presentation Title and Abstract */}
          <div className="document-option">
            <div className="document-icon">📝</div>
            <label>
              <input
                type="checkbox"
                name="presentationSubmitted"
                checked={formData.presentationSubmitted}
                onChange={handleInputChange}
              />
              <span>Presentation Title and Brief Abstract</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => presentationInputRef.current?.click()}
              disabled={uploadingFiles.presentation}
            >
              {uploadingFiles.presentation ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={presentationInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              onChange={handlePresentationUpload}
            />
            {getUploadStatus('presentation')}
          </div>

          {/* Presentation Slides */}
          <div className="document-option">
            <div className="document-icon">📊</div>
            <label>
              <input
                type="checkbox"
                name="slidesSubmitted"
                checked={formData.slidesSubmitted}
                onChange={handleInputChange}
              />
              <span>Presentation Slides (where requested)</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => slidesInputRef.current?.click()}
              disabled={uploadingFiles.slides}
            >
              {uploadingFiles.slides ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={slidesInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.ppt,.pptx,.key"
              onChange={handleSlidesUpload}
            />
            {getUploadStatus('slides')}
          </div>

          {/* Institutional Profile */}
          <div className="document-option">
            <div className="document-icon">🏛️</div>
            <label>
              <input
                type="checkbox"
                name="profileSubmitted"
                checked={formData.profileSubmitted}
                onChange={handleInputChange}
              />
              <span>Institutional Profile (where applicable)</span>
            </label>
            <button 
              type="button"
              className="upload-btn small"
              onClick={() => profileInputRef.current?.click()}
              disabled={uploadingFiles.profile}
            >
              {uploadingFiles.profile ? '⏳' : 'Upload'}
            </button>
            <input
              type="file"
              ref={profileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleProfileUpload}
            />
            {getUploadStatus('profile')}
          </div>
        </div>
        {renderError('documents')}
      </div>

      <div className="form-group">
        <label>Special Participation Requirements</label>
        <textarea
          name="specialRequirements"
          placeholder="Accessibility, dietary, interpretation, protocol or other requirements, if applicable"
          value={formData.specialRequirements}
          onChange={handleInputChange}
          rows={3}
        />
      </div>
    </div>
  );

  // ===== RENDER STEP 6: Participation Confirmation =====
  const renderStep6 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION G: Participation Confirmation</h2>
        <p>Please confirm your participation</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`form-group ${stepErrors.confirmationName ? 'has-error' : ''}`}>
        <label>Full Name *</label>
        <input
          type="text"
          name="confirmationName"
          placeholder="Enter your full name"
          value={formData.confirmationName}
          onChange={handleInputChange}
        />
        {renderError('confirmationName')}
      </div>

      <div className={`form-group ${stepErrors.confirmationAgreement ? 'has-error' : ''}`}>
        <div className="confirmation-box">
          <p>
            I, <strong>{formData.confirmationName || '____________________'}</strong>, acknowledge receipt of my official invitation, 
            nomination or appointment and hereby confirm my participation in the Africa Infrastructure, Climate Change, 
            Security & Green Investment Summit (AICIS 2026), scheduled to hold from <strong>25–28 November 2026</strong> 
            in <strong>Abuja, Nigeria</strong>, in the capacity indicated in this form.
          </p>
          <p>
            I understand that my session, presentation topic, speaking duration, programme schedule and participation 
            arrangements are subject to final confirmation by the AICIS 2026 Planning and Organising Committee.
          </p>
          <label className="confirm-checkbox">
            <input
              type="checkbox"
              name="confirmationAgreement"
              checked={formData.confirmationAgreement}
              onChange={handleInputChange}
            />
            <span>I confirm my participation in AICIS 2026</span>
          </label>
        </div>
        {renderError('confirmationAgreement')}
      </div>
    </div>
  );

  // ===== RENDER STEP 7: Media Consent =====
  const renderStep7 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION H: Media and Publication Consent</h2>
        <p>Please indicate your consent for media use</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="media-consent-box">
        <p>
          I authorise the AICIS Secretariat and Africa Business Ventures & Investment Group (ABVIG) to use my approved 
          professional information and Summit-related media materials, including my:
        </p>
        <ul>
          <li>Name, official designation and organisation</li>
          <li>Professional biography and photograph</li>
          <li>Presentation title and approved presentation materials</li>
          <li>Statements, quotations and approved excerpts</li>
          <li>Summit photographs, interviews, video and audio recordings</li>
        </ul>
        <p>
          Approved materials may be used for official Summit communication, programmes, reports, communiqués, websites, 
          publications, brochures, press releases, social-media platforms and post-event documentation.
        </p>
      </div>

      <div className={`form-group ${stepErrors.mediaConsent ? 'has-error' : ''}`}>
        <div className="radio-group">
          <label className="radio-option-card">
            <input
              type="radio"
              name="mediaConsent"
              value="full"
              checked={formData.mediaConsent === 'full'}
              onChange={handleInputChange}
            />
            <span>I CONSENT to the use of the above information and materials for official AICIS and ABVIG purposes.</span>
          </label>
          <label className="radio-option-card">
            <input
              type="radio"
              name="mediaConsent"
              value="limited"
              checked={formData.mediaConsent === 'limited'}
              onChange={handleInputChange}
            />
            <span>I CONSENT WITH THE FOLLOWING LIMITATIONS:</span>
          </label>
          {formData.mediaConsent === 'limited' && (
            <div className="other-input">
              <textarea
                name="mediaLimitations"
                placeholder="Please specify your limitations"
                value={formData.mediaLimitations}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
          )}
          <label className="radio-option-card">
            <input
              type="radio"
              name="mediaConsent"
              value="none"
              checked={formData.mediaConsent === 'none'}
              onChange={handleInputChange}
            />
            <span>I DO NOT CONSENT to public media or promotional use beyond essential Summit administration and official records.</span>
          </label>
        </div>
        {renderError('mediaConsent')}
      </div>
    </div>
  );

  // ===== RENDER STEP 8: Declaration =====
  const renderStep8 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>SECTION I: Declaration</h2>
        <p>Please complete the declaration</p>
      </div>

      {Object.keys(stepErrors).length > 0 && (
        <div className="error-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(stepErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="declaration-box">
        <p>I confirm that the information provided in this form is accurate to the best of my knowledge. I understand that participation arrangements remain subject to final confirmation by the AICIS 2026 Planning and Organising Committee.</p>
      </div>

      <div className="form-grid">
        <div className={`form-group ${stepErrors.declarationName ? 'has-error' : ''}`}>
          <label>Full Name *</label>
          <input
            type="text"
            name="declarationName"
            placeholder="Enter your full name"
            value={formData.declarationName}
            onChange={handleInputChange}
          />
          {renderError('declarationName')}
        </div>

        <div className={`form-group ${stepErrors.declarationDesignation ? 'has-error' : ''}`}>
          <label>Official Designation *</label>
          <input
            type="text"
            name="declarationDesignation"
            placeholder="Enter your official designation"
            value={formData.declarationDesignation}
            onChange={handleInputChange}
          />
          {renderError('declarationDesignation')}
        </div>

        <div className={`form-group ${stepErrors.declarationOrganization ? 'has-error' : ''}`}>
          <label>Organisation / Institution *</label>
          <input
            type="text"
            name="declarationOrganization"
            placeholder="Enter your organization"
            value={formData.declarationOrganization}
            onChange={handleInputChange}
          />
          {renderError('declarationOrganization')}
        </div>

        <div className={`form-group ${stepErrors.declarationSignature ? 'has-error' : ''}`}>
          <label>Signature *</label>
          <input
            type="text"
            name="declarationSignature"
            placeholder="Type your full name as signature"
            value={formData.declarationSignature}
            onChange={handleInputChange}
          />
          {renderError('declarationSignature')}
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            name="declarationDate"
            value={formData.declarationDate}
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );

  // ===== RENDER STEP 9: Optional Endorsement =====
  const renderStep9 = () => (
    <div className="form-step">
      <div className="step-header">
        <h2>Optional Institutional Endorsement</h2>
        <p>Complete if applicable</p>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Name and Designation of Authorised Official</label>
          <input
            type="text"
            name="endorsingOfficial"
            placeholder="Enter endorsing official name"
            value={formData.endorsingOfficial}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Institution / Organisation</label>
          <input
            type="text"
            name="endorsingInstitution"
            placeholder="Enter institution name"
            value={formData.endorsingInstitution}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Signature / Official Stamp</label>
          <input
            type="text"
            name="endorsementSignature"
            placeholder="Type signature or stamp"
            value={formData.endorsementSignature}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            name="endorsementDate"
            value={formData.endorsementDate}
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );

  // ===== RENDER STEP 10: Review and Submit =====
  const renderStep10 = () => (
    <div className="form-step review-step">
      <div className="step-header">
        <h2>Review and Submit</h2>
        <p>Please review your information before submitting</p>
      </div>

      <div className="review-summary">
        <div className="review-section">
          <h3>Personal Information</h3>
          <p><strong>Title:</strong> {formData.title || formData.otherTitle}</p>
          <p><strong>Full Name:</strong> {formData.fullName}</p>
          <p><strong>Designation:</strong> {formData.officialDesignation}</p>
          <p><strong>Organization:</strong> {formData.organization}</p>
          <p><strong>Country:</strong> {formData.country}</p>
          <p><strong>Phone:</strong> {formData.phone}</p>
          <p><strong>Email:</strong> {formData.email}</p>
        </div>

        <div className="review-section">
          <h3>Participation & Session</h3>
          <p><strong>Category:</strong> {formData.participationCategory.join(', ') || formData.otherParticipation}</p>
          <p><strong>Presentation Title:</strong> {formData.presentationTitle}</p>
          <p><strong>Thematic Area:</strong> {formData.thematicArea}</p>
          <p><strong>Mode:</strong> {formData.modeOfParticipation}</p>
        </div>

        <div className="review-section">
          <h3>Documents & Consent</h3>
          <p><strong>Documents:</strong> {
            [
              formData.bioSubmitted && 'Bio',
              formData.cvSubmitted && 'CV',
              formData.photoSubmitted && 'Photo',
              formData.presentationSubmitted && 'Abstract',
              formData.slidesSubmitted && 'Slides',
              formData.profileSubmitted && 'Profile'
            ].filter(Boolean).join(', ') || 'None'
          }</p>
          <p><strong>Media Consent:</strong> {formData.mediaConsent === 'full' ? 'Full Consent' : formData.mediaConsent === 'limited' ? 'Limited Consent' : 'No Consent'}</p>
          {formData.mediaConsent === 'limited' && <p><strong>Limitations:</strong> {formData.mediaLimitations}</p>}
        </div>

        <div className="review-section">
          <h3>Declaration</h3>
          <p><strong>Name:</strong> {formData.declarationName}</p>
          <p><strong>Designation:</strong> {formData.declarationDesignation}</p>
          <p><strong>Organization:</strong> {formData.declarationOrganization}</p>
        </div>
      </div>

      {submitError && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {submitError}
        </div>
      )}
    </div>
  );

  if (submitSuccess) {
    return (
      <div className="speakers-form-page">
        <div className="form-container success-container">
          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h2>Form Submitted Successfully!</h2>
            <p>Thank you for confirming your participation in AICIS 2026.</p>
            <p>Your submission has been received and will be reviewed by the AICIS Planning and Organising Committee.</p>
            <p className="success-detail">You will receive a confirmation email shortly.</p>
            <div className="success-actions">
              <Link to="/activities/speakers" className="btn btn-primary">
                ← Back to Speakers
              </Link>
              <Link to="/" className="btn btn-secondary">
                Go to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="speakers-form-page">
      <div className="form-container">
        <div className="form-header">
          <div className="form-brand">
            <span className="form-badge">AICIS 2026</span>
            <h1>Speaker, Panellist & Resource Person</h1>
            <h2>Participation Confirmation, Professional Profile and Media Consent Form</h2>
            <p className="form-subtitle">
              Please complete all applicable sections and return the signed form with a recent professional photograph and curriculum vitae.
            </p>
            <div className="form-meta">
              <span>📅 25–28 November 2026</span>
              <span>📍 Abuja, Nigeria</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {renderStepIndicator()}

          <div className="form-body">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
            {currentStep === 7 && renderStep7()}
            {currentStep === 8 && renderStep8()}
            {currentStep === 9 && renderStep9()}
            {currentStep === 10 && renderStep10()}
          </div>

          <div className="form-navigation">
            {currentStep > 1 && (
              <button type="button" className="btn btn-prev" onClick={prevStep}>
                ← Previous
              </button>
            )}
            {currentStep < 10 && (
              <button type="button" className="btn btn-next" onClick={nextStep}>
                Next →
              </button>
            )}
            {currentStep === 10 && (
              <button type="submit" className="btn btn-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            )}
          </div>

          <div className="form-footer">
            <p>Page {currentStep} of 10</p>
            <p className="form-required">* Required fields</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpeakersForm;